import { errorMessage } from "@atlas/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, FileArchive } from "lucide-react";
import type { DataExport } from "@atlas/shared";
import { fetchDataExport, requestDataExport } from "../api/account";
import { AtlasPanel } from "./AtlasPanel";
import { Button } from "./Button";

// ─── "Your data" panel on /profile (spec 17-data-export.md) ──────────────────
// Request → poll every 15 s while the worker builds the archive → Download
// (15-minute presigned link, refetched on click so it is never stale).

const POLL_MS = 15_000;

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isInFlight(exp: DataExport | null | undefined): boolean {
  return exp?.status === "pending" || exp?.status === "processing";
}

function statusLine(exp: DataExport): string {
  switch (exp.status) {
    case "pending":
    case "processing":
      return `Preparing your export (requested ${formatDateTime(exp.requestedAt)}). We'll email you when it's ready.`;
    case "ready": {
      const parts = [`Ready until ${exp.expiresAt ? formatDate(exp.expiresAt) : "expiry"}`];
      if (exp.activityCount !== null) parts.push(`${exp.activityCount} activities`);
      if (exp.sizeBytes !== null) parts.push(formatBytes(exp.sizeBytes));
      return parts.join(" · ");
    }
    case "expired":
      return `Your last export (${exp.completedAt ? formatDate(exp.completedAt) : "earlier"}) has expired. Request a new one.`;
    case "failed":
      return "Your last export failed. Try again, and let us know if it keeps happening.";
  }
}

export function DataExportPanel() {
  const queryClient = useQueryClient();

  const exportQuery = useQuery({
    queryKey: ["account/export"],
    queryFn: fetchDataExport,
    refetchInterval: (query) => (isInFlight(query.state.data?.export) ? POLL_MS : false),
  });

  const requestMutation = useMutation({
    mutationFn: requestDataExport,
    onSuccess: (data) => {
      queryClient.setQueryData(["account/export"], data);
    },
  });

  const current = exportQuery.data?.export ?? null;
  const inFlight = isInFlight(current);

  async function handleDownload() {
    // The link lives 15 minutes; refetch so a page left open still works.
    const fresh = await queryClient.fetchQuery({ queryKey: ["account/export"], queryFn: fetchDataExport });
    const url = fresh.export?.downloadUrl;
    if (url) window.location.assign(url);
  }

  return (
    <AtlasPanel title="Your data">
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
          <FileArchive size={18} style={{ color: "var(--text-accent)", flexShrink: 0, marginTop: 2 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)", marginBottom: 4 }}>
              Download a copy of everything Atlas holds about you
            </div>
            <div style={{ fontSize: 13, color: "var(--text-muted)", lineHeight: 1.5 }}>
              A zip with a GPX file for every activity that has a track, plus your logs,
              achievements, collections progress, lists, posts, comments, check-ins and profile
              as JSON. Building it takes a few minutes; we email you when it's ready and the
              file stays available for 7 days. One export every 24 hours.
            </div>
          </div>
        </div>

        {current && (
          <div
            style={{
              fontSize: 13,
              color: current.status === "failed" ? "var(--status-danger)" : "var(--text-secondary)",
              padding: "10px 12px",
              background: "var(--surface-sunken)",
              borderRadius: "var(--radius-md)",
            }}
            aria-live="polite"
          >
            {statusLine(current)}
          </div>
        )}

        {requestMutation.isError && (
          <div style={{ fontSize: 13, color: "var(--status-danger)" }}>
            {errorMessage(requestMutation.error, "Could not request an export")}
          </div>
        )}

        <div className="r-wrap" style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          {current?.status === "ready" && (
            <Button size="sm" leftIcon={<Download size={14} />} onClick={() => void handleDownload()}>
              Download zip
            </Button>
          )}
          <Button
            variant={current?.status === "ready" ? "ghost" : "secondary"}
            size="sm"
            disabled={inFlight || requestMutation.isPending || exportQuery.isLoading}
            onClick={() => requestMutation.mutate()}
          >
            {inFlight ? "Preparing…" : current?.status === "ready" ? "Request a new export" : "Request export"}
          </Button>
        </div>
      </div>
    </AtlasPanel>
  );
}
